import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { SnackbarSeverityType } from '../../../enums';
import DropdownSelect from '../../../components/DropdownSelect';
import {
  generalConductOptions,
  progressOptions,
  reasonOfLeavingOptions
} from '../config/constant';
import { GenerateLcFormWrapper } from '../styled';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import CustomDatePicker from '../../../components/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';

interface GenerateLcFormProps {
  lcData: any;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  generateLC: (data: GenerateLcFormInputs) => Promise<void>;
}

type GenerateLcFormInputs = {
  progress: string;
  generalConduct: string;
  reasonOfLeaving: string;
  remarks: string;
  dateOfLeaving: string;
  detainedInClass: string;
  passedAndPromotedToClass: string;
};

const GenerateLcForm: React.FC<GenerateLcFormProps> = ({
  lcData,
  showToast,
  generateLC
}) => {
  const [progress, setProgress] = useState(lcData.progress || '');
  const [generalConduct, setGeneralConduct] = useState(
    lcData.generalConduct || ''
  );
  const [reasonOfLeaving, setReasonOfLeaving] = useState(
    lcData.reasonOfLeaving || ''
  );
  const [remarks, setRemarks] = useState(lcData.remarks || '');
  const [dateOfLeaving, setDateOfLeaving] = useState<Dayjs | null>(
    lcData.dateOfLeaving ? dayjs(lcData.dateOfLeaving, 'DD-MM-YYYY') : null
  );
  const [detainedInClass, setDetainedInClass] = useState(
    lcData.detainedInClass || ''
  );
  const [passedAndPromotedToClass, setPassedAndPromotedToClass] = useState(
    lcData.passedAndPromotedTo || ''
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [errors, setErrors] = useState<Partial<GenerateLcFormInputs>>({});

  const validateForm = () => {
    const newErrors: Partial<GenerateLcFormInputs> = {};
    if (!progress) newErrors.progress = 'Progress is required';
    if (!generalConduct)
      newErrors.generalConduct = 'General Conduct is required';
    if (!reasonOfLeaving)
      newErrors.reasonOfLeaving = 'Reason Of Leaving is required';
    if (!dateOfLeaving) newErrors.dateOfLeaving = 'Date Of Leaving is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    setIsSubmitting(true);
    event.preventDefault();
    if (!validateForm()) {
      showToast(
        'Please fill in all required fields',
        SnackbarSeverityType.error
      );
      return;
    }

    const formData: GenerateLcFormInputs = {
      progress,
      generalConduct,
      reasonOfLeaving,
      remarks,
      dateOfLeaving: dateOfLeaving ? dateOfLeaving.format('YYYY-MM-DD') : '',
      detainedInClass,
      passedAndPromotedToClass
    };
    await generateLC(formData);
    setIsSubmitting(false);
  };

  return (
    <GenerateLcFormWrapper>
      <DropdownSelect
        label='Progress'
        placeholder='Select Progress'
        name='progress'
        value={progress}
        options={progressOptions}
        onChange={(e) => setProgress(e.target.value as string)}
        error={!!errors.progress}
        helperText={errors.progress}
      />

      <DropdownSelect
        label='General Conduct'
        placeholder='Select General Conduct'
        name='generalConduct'
        value={generalConduct}
        options={generalConductOptions}
        onChange={(e) => setGeneralConduct(e.target.value as string)}
        error={!!errors.generalConduct}
        helperText={errors.generalConduct}
      />

      <DropdownSelect
        label='Reason Of Leaving'
        placeholder='Select Reason Of Leaving'
        name='reasonOfLeaving'
        value={reasonOfLeaving}
        options={reasonOfLeavingOptions}
        onChange={(e) => setReasonOfLeaving(e.target.value as string)}
        error={!!errors.reasonOfLeaving}
        helperText={errors.reasonOfLeaving}
      />

      <TextInputWithIcon
        label='Remarks'
        placeholder='Enter Remarks'
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        error={!!errors.remarks}
        helperText={errors.remarks}
        fullWidth
        hasTopLabel
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CustomDatePicker
          label='Date Of Leaving'
          value={dateOfLeaving}
          onChange={(newValue: any) => {
            setDateOfLeaving(newValue);
          }}
        />
      </LocalizationProvider>

      <TextInputWithIcon
        label='Detained In Class'
        placeholder='Enter Detained In Class'
        value={detainedInClass}
        onChange={(e) => setDetainedInClass(e.target.value)}
        error={!!errors.detainedInClass}
        helperText={errors.detainedInClass}
        fullWidth
        hasTopLabel
      />

      <TextInputWithIcon
        label='Passed And Promoted To Class'
        placeholder='Enter Passed And Promoted To Class'
        value={passedAndPromotedToClass}
        onChange={(e) => setPassedAndPromotedToClass(e.target.value)}
        error={!!errors.passedAndPromotedToClass}
        helperText={errors.passedAndPromotedToClass}
        fullWidth
        hasTopLabel
      />

      <Button
        type='submit'
        onClick={handleSubmit}
        variant='contained'
        color='primary'
        fullWidth
        sx={{ marginTop: 2 }}
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <CircularProgress size={24} color='inherit' />
        ) : (
          'Generate LC'
        )}
      </Button>
    </GenerateLcFormWrapper>
  );
};

export default GenerateLcForm;
